'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import { statics } from '@/data/store';
import FilterTable from './FilterTable';
import { COLORS } from '@/constants/colors';
import LoadingOverlay from '@/components/LoadingOverlay';
import FAQSection from '@/components/FAQSection';
import { airCleanerFaqs } from '@/data/faqData';


const tableHeader = [
  {
    id: 0,
    name: 'title',
    label: 'Filter Number',
    maxWidth: 156,
  },
  {
    id: 1,
    name: 'filter_height_inch',
    label: 'Height (H)',
    maxWidth: 139,
  },
  {
    id: 2,
    name: 'assemblie_mounting_dia_inch',
    label: 'Assembly Mounting Diameter (Inch)',
    maxWidth: 270,
  },
  {
    id: 3,
    name: 'assemblie_type',
    label: 'Assembly Type',
    maxWidth: 249,
  },
  {
    id: 4,
    name: 'assemblie_installed_height_inch',
    label: 'Assembly Installed Height (Inch)',
    maxWidth: 126,
  },
  {
    id: 5,
    name: 'filter_color',
    label: 'Color',
    maxWidth: 126,
  },
];

const ACElement1 = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getTableData();
  }, []);

  const getTableData = async () => {
    setLoading(true);
    const product_type_id = 1;

    const url = `${statics.BaseUrl}/product?product_type_id=${product_type_id}&pageNo=1&pageSize=200&is_active=1`;

    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.ResponseCode === 'Success') {
        const rows = Array.isArray(data?.data) ? data.data : data?.data?.rows || [];
        setTableData(rows);
      }
    } catch (error) {
      console.error('Failed to fetch air cleaner assemblies:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay loading={loading} />

      <ACElement1Wrapper>
        <PageTitle>Air Cleaner Assembly</PageTitle>

        <IntroText>
          Discover our complete selection of high flow air cleaner and air cleaner assembly products for performance
          applications. Whether you need an engine air cleaner assembly, custom air cleaner kit, or universal air
          cleaner assemblies, Green Filter has you covered. Each air cleaner filter element features our air cleaner
          housing assembly and air cleaner filter housing designed for maximum airflow. All made in the USA with our
          lifetime warranty.
        </IntroText>

        <div
          style={{
            textAlign: 'center',
            float: 'left',
            width: '30%',
            minWidth: 300,
          }}
        >
          <Image className="resize" alt="Air Cleaner Assembly" src="/assets/cylinders/round.webp" width={200} height={172} />
        </div>

        <div
          style={{
            textAlign: 'center',
            float: 'left',
            width: '20%',
            minWidth: 300,
          }}
        >
          <center>
            <a href="/assets/cylinders/round_filter_measurements.webp" style={{ color: COLORS.brandGreen }}>
              <Image src="/assets/cylinders/round_filter_measurements.webp" className="resize" alt="Filter Measurements" width={454} height={484} />
            </a>
            <br />
            <a
              href="/assets/cylinders/round_filter_measurements.webp"
              style={{ color: COLORS.brandGreen }}
              title="Green Filter USA - Air Cleaner Assembly Dimensions"
            >
              Click here
            </a>{' '}
            for a picture showing how air cleaner assemblies are measured.
          </center>
        </div>
        <div style={{ clear: 'both', display: 'table' }}></div>
        <br />

        <h4 style={{ padding: '0 10px' }}>
          Select an air cleaner assembly from the list below. The list can be sorted by clicking on a column heading. Clicking the heading again will sort it in the opposite direction. The table will scroll left and right for smaller screens.
        </h4>
        <p style={{ textAlign: 'center' }}>
          <strong>All measurements are in inches.</strong>
        </p>

        <FilterTable headers={tableHeader} tableData={tableData} />

        <ClosingSection>
          <ClosingText>
            Need help finding the right air cleaner assembly? Use our{' '}
            <a href="/shop-by-vehicle">vehicle selector</a> or{' '}
            <a href="/contact-us">contact us</a> for expert guidance on air cleaner assemblies.
          </ClosingText>
        </ClosingSection>
      </ACElement1Wrapper>

      <FAQSection faqs={airCleanerFaqs} />
    </>
  );
};

export default React.memo(ACElement1);

const IntroText = styled.p`
  max-width: 800px;
  margin: 0 auto 30px;
  padding: 0 20px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

const ClosingSection = styled.div`
  max-width: 800px;
  margin: 30px auto 50px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  text-align: center;

  @media (max-width: 767px) {
    margin: 20px auto 30px;
    padding: 15px;
  }
`;

const ClosingText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 15px;
  color: #555;
  margin: 0;
  line-height: 1.6;

  a {
    color: ${COLORS.brandGreen};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const PageTitle = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 38px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 20px;

  @media (max-width: 767px) {
    font-size: 26px;
    margin: 0 0 16px;
  }
`;

const ACElement1Wrapper = styled.div`
  padding: 20px;
  background: #fff;

  h2 {
    font-family: Lato, sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 30px;
  }

  h4 {
    font-family: Lato, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #333;
    line-height: 1.6;
    margin-bottom: 20px;
  }

  p {
    font-family: Lato, sans-serif;
    font-size: 15px;
    color: #333;
    margin-bottom: 30px;
  }

  .resize {
    max-width: 100%;
    height: auto;
  }

  center {
    font-family: Lato, sans-serif;
    font-size: 14px;

    a {
      font-weight: 600;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media (max-width: 767px) {
    padding: 15px;

    h2 {
      font-size: 22px;
      margin-top: 60px !important;
      margin-left: 0 !important;
    }

    h4 {
      font-size: 14px;
    }

    div[style*='float: left'] {
      float: none !important;
      width: 100% !important;
      min-width: auto !important;
      margin-bottom: 20px;
    }
  }
`;
